import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import dashboardEmployeeApi from 'api/dashboardEmployeeApi';
import { TimesheetAdjustmentReport } from 'types';
import { exportToCSV } from 'utils/common';
import HeadcountWorkingTimeChart from './HeadcountWorkingTimeChart';
import HeadcountWorkingTimeFilters, {
  HeadcountWorkingTimePayload,
} from './HeadcountWorkingTimeFilters';

const StyledContainer = styled.div`
  padding: 16px 24px;
  border: 0.5px solid var(--cl_gray200);
  border-radius: 12px;
  background-color: var(--cl_white);
  height: auto;
`;

const StyledHeaderTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  font-family: Roboto;
  line-height: 20px;
  color: #121926;
`;

const StyledWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 10px;
`;

export function HeadcountByWorkingTime() {
  const { t } = useTranslation(['dashboard']);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [dataChart, setDataChart] = useState<TimesheetAdjustmentReport>({
    labels: [],
    datasets: [],
  });

  const [filters, setFilters] = useState<{ locationId: string }>({
    locationId: '0',
  });

  useEffect(() => {
    async function fetchHeadcountByLine() {
      try {
        setIsLoading(true);
        const response = await dashboardEmployeeApi.getHeadcountWorkingTime(filters);

        const final_labels = response.data.labels.map((label: string, index: number) => {
          if (index <= 1) {
            return label + ` year`;
          }
          return label + ` years`;
        });

        const labels = final_labels;
        const datasets = [
          {
            label: '',
            data: response.data.datasets.map((x: any) => x),
            fill: true,
            backgroundColor: '#4472C4',
            borderColor: '#4472C4',
            barThickness: 20,
            borderRadius: 4,
          },
        ];

        setIsLoading(false);
        setDataChart((prevState) => ({
          ...prevState,
          labels,
          datasets,
        }));
      } catch (error) {
        setIsLoading(false);
      }
    }

    if (filters?.locationId) {
      fetchHeadcountByLine();
    }
  }, [filters, filters?.locationId]);

  const handleSubmit = ({ ...payload }: Partial<HeadcountWorkingTimePayload>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...payload,
    }));
  };

  const handleExport = async (location: { value: string; label: string }) => {
    try {
      setIsLoadingExport(true);
      const response = await dashboardEmployeeApi.exportHeadcountWorkingTime(filters);
      setIsLoadingExport(false);

      const exportFileName = `Headcount_By_Working_Time_${location.label}.xlsx`;
      exportToCSV(response, exportFileName);
    } catch (error) {
      setIsLoadingExport(false);
    }
  };

  return (
    <StyledContainer>
      <StyledWrapper>
        <StyledHeaderTitle>{t('dashboard_employee.headcount_workingtime')}</StyledHeaderTitle>

        <HeadcountWorkingTimeFilters
          onSubmit={handleSubmit}
          loadingExport={isLoadingExport}
          handleExport={handleExport}
          initialValues={{}}
        />
      </StyledWrapper>

      <HeadcountWorkingTimeChart loading={isLoading} dataChart={dataChart} />
    </StyledContainer>
  );
}
